/**
 * @namespace transat/leave-request
 */
import { VARIANT } from '~/utils/lib/layout/ui'
import { __, both, compose, concat, concatAll, equals, includes, isEmpty, Maybe, not, notEmpty, notEquals, pipe, pipe_, prop, propEq, reject, typeOf, unless, values, when } from '~/utils/lib/core/prelude'
import { wrap } from '~/components/ui/form/Tree.vue'
import { REMOTE_WORK_LEAVE_TYPE } from '../remote-work'

/**
 * @typedef {object} LeaveRequest
 * @property {string} grouped_leave_id PK
 * @property {STATUS} status
 * @property {STATUS_} status_
 * @property {LEAVE_TYPE} leave_type
 * @property {LEAVE_TYPE_} leave_type_
 * @property {string} created_at ISO 8601
 * @property {string} updated_at ISO 8601
 * @property {string} from ISO 8601
 * @property {string} to ISO 8601
 * @property {number} days eg. `0.5`
 * @property {CLOCK_PERIOD} start_time
 * @property {CLOCK_PERIOD} end_time
 * @property {import('~/stores/transat/collaborator').Collaborator} collaborator
 * @property {import('~/stores/transat/service').Service["name"]} service_name
 * @property {import('~/stores/transat/service').Service["uuidTeam"]} service_uuidTeam
 * @property {string} [comment]
 * @property {string} [closure_reason] either from REJECTED or CANCELLED action
 * @property {string} [response_date] ISO 8601
 * @property {string} [leave_import_date_on_sp] ISO 8601
 * @property {boolean} [is_on_transat]
 * @property {string} [uuid_paie]
 * @property {ProofDocumentEmpty|ProofDocumentLastVersion} [proof_document]
 * @property {LeaveRequestMiscLeave} [miscLeave]
 *
 *
 * @typedef {object} LeaveRequestMiscLeave
 * @property {import('~/stores/transat/leave-request/misc').MiscLeaveRule["id"]} id
 * @property {import('~/stores/transat/leave-request/misc').MiscLeaveRule["label"]} label
 * @property {string} event_date ISO 8601
 * @property {import('~/stores/transat/leave-request/misc').MiscLeaveRule["day_count"]} day_count
 * @property {import('~/stores/transat/leave-request/misc').MiscLeaveRule["day_count_mode_label"]} day_count_mode_label
 * @property {import('~/stores/transat/leave-request/misc').MiscLeaveRule["day_count_mode"]} day_count_mode
 * @property {import('~/stores/transat/leave-request/misc').MiscLeaveRule["remuneration_type"]} remuneration_type
 * @property {import('~/stores/transat/leave-request/misc').MiscLeaveRule["remuneration_type_label"]} remuneration_type_label
 */

/**
 * @typedef {object} ProofDocumentEmpty
 * @property {PROOF_STATUS} status
 *    PROOF_STATUS.NOT_YET_UPLOADED | PROOF_STATUS.NOT_NEEDED
 * @property {string} created_at ISO 8601
 * @property {string} updated_at ISO 8601
 *
 *
 * @typedef {object} ProofDocumentLastVersion
 * @property {PROOF_STATUS} status
 * @property {string} created_at ISO 8601
 * @property {string} updated_at ISO 8601
 * @property {string} uuid_document one per `LeaveRequest` (FK)
 * @property {string} version one per document uploaded
 */

export const ERRORS = {
  NO_DAYS_ESTIMATION_MODE_FOR_RTT_LEAVE_TYPE_ : 'no_days_estimation_mode_for_rtt_leave_type_',
  NO_DAYS_ESTIMATION_MODE_FOR_COMPENSATORY_LEAVE_TYPE_ : 'no_days_estimation_mode_for_compensatory_leave_type_',
}

/**
 * @enum {typeof CLOCK_PERIOD[keyof typeof CLOCK_PERIOD]}
 * @readonly
 */
export const CLOCK_PERIOD = /** @type {const} */ ({
  AM: 'AM',
  PM: 'PM'
})
/**
 * @enum {typeof LEAVE_TYPE[keyof typeof LEAVE_TYPE]}
 * @readonly
 */
export const LEAVE_TYPE = /** @type {const} */ ({
  // ie. "congés payés n", "congés acquis"
  EARNED_PAID_LEAVE: 'earned-paid-leave',
  // ie. "congés payés n-1", "congés (acquis) à prendre"
  CURRENT_PAID_LEAVE: 'current-paid-leave',
  RTT: 'rtt-paid-leave',
  MISC: 'other-leave',
  COMPENSATORY_LEAVE: 'compensatory-leave',
})
/** @type {(type: LEAVE_TYPE) => string} */
export const printLeaveType = type => {
  switch (type) {
    case LEAVE_TYPE.RTT:                return 'RTT'
    case LEAVE_TYPE.CURRENT_PAID_LEAVE: return 'Congés à prendre'
    case LEAVE_TYPE.EARNED_PAID_LEAVE:  return 'Congés acquis'
    case LEAVE_TYPE.MISC:               return 'Autres'
    case LEAVE_TYPE.COMPENSATORY_LEAVE: return 'Repos compensateurs'
  }
}
/**
 * @enum {typeof LEAVE_TYPE_[keyof typeof LEAVE_TYPE_]}
 * @desc Decorating <LEAVE_TYPE> to handle a unique value for paid leave
 */
export const LEAVE_TYPE_ = /** @type {const} */ ({
  PAID_LEAVE: 'paid-leave',
  RTT: 'rtt-paid-leave',
  MISC: 'misc-paid-leave',
  COMPENSATORY_LEAVE: 'compensatory-leave',
})
/** @type {(type: LEAVE_TYPE) => LEAVE_TYPE_} */
export const castToLeaveType_ = type => {
  switch (type) {
    case LEAVE_TYPE.RTT:
      return LEAVE_TYPE_.RTT

    case LEAVE_TYPE.CURRENT_PAID_LEAVE:
    case LEAVE_TYPE.EARNED_PAID_LEAVE:
      return LEAVE_TYPE_.PAID_LEAVE

    case LEAVE_TYPE.MISC:
      return LEAVE_TYPE_.MISC

    case LEAVE_TYPE.COMPENSATORY_LEAVE:
      return LEAVE_TYPE_.COMPENSATORY_LEAVE
  }
}
/** @type {(type: LEAVE_TYPE_) => Array<LEAVE_TYPE>} */
export const castToLeaveType = type => {
  switch (type) {
    case LEAVE_TYPE_.RTT:
      return [ LEAVE_TYPE.RTT ]

    case LEAVE_TYPE_.PAID_LEAVE:
      return [ LEAVE_TYPE.CURRENT_PAID_LEAVE, LEAVE_TYPE.EARNED_PAID_LEAVE ]

    case LEAVE_TYPE_.MISC:
      return [ LEAVE_TYPE.MISC ]

    case LEAVE_TYPE_.COMPENSATORY_LEAVE:
      return [ LEAVE_TYPE.COMPENSATORY_LEAVE ]
  }
}
/** @type {(type_: LEAVE_TYPE_) => string} */
export const printLeaveType_ = type_ => {
  switch (type_) {
    case LEAVE_TYPE_.RTT:         return 'RTT'
    case LEAVE_TYPE_.PAID_LEAVE:  return 'CP'
    case LEAVE_TYPE_.MISC:        return 'Autre'
    case LEAVE_TYPE_.COMPENSATORY_LEAVE: return 'Repos compensateurs'
  }
}
/** @type {(type_: LEAVE_TYPE_) => string} */
export const playschoolPrintLeaveType_ = type_ => {
  switch (type_) {
    case LEAVE_TYPE_.RTT:         return ``
    case LEAVE_TYPE_.PAID_LEAVE:  return `Congés payés`
    case LEAVE_TYPE_.MISC:        return ``
    case LEAVE_TYPE_.COMPENSATORY_LEAVE: return ``
  }
}

/** @type {(type_: LEAVE_TYPE_ | import('~/stores/transat/remote-work').REMOTE_WORK_LEAVE_TYPE, parametersModule: import('~/stores/transat/parameters/module').LeaveModuleInterface) => string} */
export const printLeaveTypeWithOptions = (type_, parametersModule) => {
  switch (type_) {
    case LEAVE_TYPE_.COMPENSATORY_LEAVE:
      return parametersModule.rc_conversion.short_name

    case REMOTE_WORK_LEAVE_TYPE:
      return 'TT'

    default:
      return printLeaveType_(type_)
  }
}
/** @type {(type_: LEAVE_TYPE_ | import('~/stores/transat/remote-work').REMOTE_WORK_LEAVE_TYPE, parametersModule: import('~/stores/transat/parameters/module').LeaveModuleInterface) => string} */
export const playschoolPrintLeaveTypeWithOptions = (type_, parametersModule) => {
  switch (type_) {
    case LEAVE_TYPE_.COMPENSATORY_LEAVE:
      return parametersModule.rc_conversion.long_name

    case REMOTE_WORK_LEAVE_TYPE:
      return 'Télétravail'

    default:
      return playschoolPrintLeaveType_(type_)
  }
}

/**
 * @desc Generate leave options for wizard
 *  - Misc is available if enabled
 *  - RC is available if configured and enabled
 *  - RTT is available if enabled
 * @type {(parametersModule: import('~/stores/transat/parameters/module').LeaveModuleInterface, miscLeaveFlag :boolean) => Array<import('~/components/ui/form/Tree.vue').UITreeOption>}
 */
export const generateLeaveOptionsForWizard = (parametersModule, miscLeaveFlag) => {
  return pipe_(
    values(LEAVE_TYPE_),
    unless(
      () => miscLeaveFlag,
      reject(
        (leaveType_) => equals(leaveType_, LEAVE_TYPE_.MISC)
      )
    ),
    unless(
      () => parametersModule.rc_conversion.allowed,
      reject(
        (leaveType_) => equals(leaveType_, LEAVE_TYPE_.COMPENSATORY_LEAVE)
      )
    ),
    unless(
      () => parametersModule.rtt_acquisition.allowed,
      reject(
        (leaveType_) => equals(leaveType_, LEAVE_TYPE_.RTT)
      )
    ),
    wrap
  )
}

/**
 * @desc Generate leave options for filters
 *  - RTT is always available
 *  - RC is available if configured and enabled
 * @type {(parametersModule: import('~/stores/transat/parameters/module').LeaveModuleInterface, miscLeaveFlag :boolean, remoteWorkFlag :boolean) => Array<import('~/components/ui/form/Tree.vue').UITreeOption>}
 */
export const generateLeaveOptionsForFilters = (parametersModule, miscLeaveFlag, remoteWorkFlag) => {
  return pipe_(
    values(LEAVE_TYPE_),
    unless(
      () => miscLeaveFlag,
      reject(
        (leaveType_) => equals(leaveType_, LEAVE_TYPE_.MISC)
      )
    ),
    unless(
      () => parametersModule.rc_conversion.allowed,
      reject(
        (leaveType_) => equals(leaveType_, LEAVE_TYPE_.COMPENSATORY_LEAVE)
      )
    ),
    when(
      () => parametersModule.remote_work_settings?.allowed && remoteWorkFlag,
      concat([ REMOTE_WORK_LEAVE_TYPE ])
    ),
    wrap
  )
}

/**
 * @enum {typeof STATUS[keyof typeof STATUS]}
 * @readonly
 */
export const STATUS = /** @type {const} */ ({
  PENDING: 'waiting',
  APPROVED: 'accepted',
  CANCELLED_BY_EMPLOYER: 'canceled_by_employer',
  CANCELLED_BY_EMPLOYEE: 'canceled_by_employee',
  REJECTED: 'rejected',
})
/**
 * @enum {typeof STATUS_[keyof typeof STATUS_]}
 * @desc Decorating <STATUS> to handle a unique value for cancellation
 */
export const STATUS_ = /** @type {const} */ ({
  PENDING: 'pending',
  APPROVED: 'accepted',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected',
})
/** @type {(status: STATUS) => STATUS_} */
export const castToStatus_ = status => {
  switch (status) {
    case STATUS.APPROVED: return STATUS_.APPROVED
    case STATUS.PENDING:  return STATUS_.PENDING
    case STATUS.REJECTED: return STATUS_.REJECTED

    case STATUS.CANCELLED_BY_EMPLOYEE:
    case STATUS.CANCELLED_BY_EMPLOYER:
      return STATUS_.CANCELLED
  }
}
/** @type {(status_: STATUS_) => Array<STATUS>} */
export const castToStatus = status_ => {
  switch (status_) {
    case STATUS_.APPROVED:  return [STATUS.APPROVED]
    case STATUS_.PENDING:   return [STATUS.PENDING]
    case STATUS_.REJECTED:   return [STATUS.REJECTED]

    case STATUS_.CANCELLED:
      return [ STATUS.CANCELLED_BY_EMPLOYEE, STATUS.CANCELLED_BY_EMPLOYER ]
  }
}
/** @type {(status_: STATUS_) => string} */
export const printStatus_ = status_ => {
  switch (status_) {
    case STATUS_.APPROVED:  return 'Validée'
    case STATUS_.PENDING:   return 'En attente'
    case STATUS_.CANCELLED: return 'Annulée'
    case STATUS_.REJECTED:  return 'Refusée'
  }
}
/**
 * @type {(status_: STATUS_) => string}
 * @desc Best used with `UI.ICON_THEME.FILLED`
 */
export const getStatus_Icon = status_ => {
  switch (status_) {
    case STATUS_.APPROVED:  return 'done'
    case STATUS_.PENDING: return 'fiber_manual_record'
    case STATUS_.REJECTED:  return 'close'
    case STATUS_.CANCELLED: return 'block'
  }
}
/**
 * @enum {typeof DAYS_CALCULATION[keyof typeof DAYS_CALCULATION]}
 * @readonly
 */
export const DAYS_CALCULATION = /** @type {const} */ ({
  OUVRES: 'ouvrés',
  OUVRABLES: 'ouvrables',
})

export const getListRoute = (
  /** @type {import('~/stores/client/access').Structure["id"]} */ structure_id,
  /** @type {import('~/stores/client/access').Company["code_societe"]} */ code_societe,
  ) => concatAll([
    `/data/${structure_id}`,
    `/conges`,
    `/leave-request/list`,
    `?company=${encodeURIComponent(code_societe)}`
  ])

/**
 * @desc Whether or not a `LeaveRequest` can be approved
 * @type {(_: LeaveRequest) => Boolean}
 */
export const hasApprovedAction = propEq('status_', STATUS_.PENDING)
/**
 * @desc Whether or not a `LeaveRequest` can be approved _with_
 *    an optional reason (added rules on #SP-4234)
 * @type {(_: LeaveRequest) => Boolean}
 */
export const hasApprovedActionWithReason = both(
  hasApprovedAction,
  propEq('leave_type_', LEAVE_TYPE_.MISC)
)
/**
 * @desc Whether or not a `LeaveRequest` can be rejected
 *    (added rules on #SP-4233)
 * @type {(_: LeaveRequest) => Boolean}
 */
export const hasRejectedAction = both(
  propEq('status_', STATUS_.PENDING),
  pipe(
    // @ts-expect-error
    prop('leave_type_'),
    includes(__, [ LEAVE_TYPE_.PAID_LEAVE, LEAVE_TYPE_.RTT, LEAVE_TYPE_.COMPENSATORY_LEAVE ])
  )
)
/**
 * @desc Whether or not a `LeaveRequest` can be cancelled (see #SP-2855)
 * @type {(_: LeaveRequest) => Boolean}
 */
export const hasCancelledAction = both(
  propEq('status_', STATUS_.APPROVED),
  compose(isEmpty, prop('uuid_paie'))
)
/**
 * @type {(_: LEAVE_TYPE) => boolean}
 * @desc #SP-4297 (eg. "2 days {of something?}")
 */
export const hasDaysCalculation = both(notEquals(LEAVE_TYPE.RTT), notEquals(LEAVE_TYPE.COMPENSATORY_LEAVE))
/**
 * @type {(_: { toPluralForm: boolean; toFeminineForm: boolean; }, type: DAYS_CALCULATION) => string}
 */
export const printDaysCalculation = ({ toPluralForm, toFeminineForm }, type) => {
  const radix = (() => {
    switch (type) {
      case DAYS_CALCULATION.OUVRABLES:  return 'ouvrable'
      case DAYS_CALCULATION.OUVRES:     return 'ouvré'
    }
  })()

  const genderForm = (() => {
    if (not(toFeminineForm))
      return ''

    switch (type) {
      case DAYS_CALCULATION.OUVRABLES:  return ''
      case DAYS_CALCULATION.OUVRES:     return 'e'
    }
  })()

  const pluralForm = (() => {
    if (not(toPluralForm))
      return ''

    return 's'
  })()

  return concatAll([
    radix,
    genderForm,
    pluralForm
  ])
}
/**
 * @type {(_: LEAVE_TYPE_) => boolean}
 * @desc #SP-4297 + #SP-4861  (eg. "2 days {of something?}")
 * @SHAME: this should be a remote business logic
 */
export const hasDaysAdjectival = both(notEquals(LEAVE_TYPE_.RTT), notEquals(LEAVE_TYPE_.COMPENSATORY_LEAVE))
/**
 * @desc #SP-6841 (used with `hasDaysAdjectival` predicate)
 * @SHAME: this should be a remote business logic
 * @throws {Error}
 * @param {LEAVE_TYPE_} leaveType_
 * @return {DAY_ESTIMATION_MODE}
 */
export function getDayEstimationMode(leaveType_) {
  switch (leaveType_) {

    case LEAVE_TYPE_.MISC:
      // ie. use `stores::transat::leave-request::printDaysCalculation()`
      return DAY_ESTIMATION_MODE.MISC_DAY_COUNT_MODE

    case LEAVE_TYPE_.PAID_LEAVE:
      // ie. use `stores::transat::parameters::misc-leave::printDayCountMode_()`
      return DAY_ESTIMATION_MODE.COMPANY_DAY_CALCULATION_MODE

    case LEAVE_TYPE_.RTT:
      // (cf. `stores::transat::leave-request::hasDaysAdjectival()`)
      throw new Error(ERRORS.NO_DAYS_ESTIMATION_MODE_FOR_RTT_LEAVE_TYPE_)

    case LEAVE_TYPE_.COMPENSATORY_LEAVE:
      // no estimation for compensatory leave
      throw new Error(ERRORS.NO_DAYS_ESTIMATION_MODE_FOR_COMPENSATORY_LEAVE_TYPE_)
  }
}
/**
 * @type {(_: LeaveRequest) => boolean}
 * @desc Type predicate function for `LeaveRequestMiscLeave`
 */
export const hasLeaveRequestMiscLeave = leaveRequest => {
  return leaveRequest.leave_type === LEAVE_TYPE.MISC
}
/**
 * @type {(_: CLOCK_PERIOD) => string}
 */
export const printClockPeriod = clockPeriod => {
  switch (clockPeriod) {
    case CLOCK_PERIOD.AM: return 'matin'
    case CLOCK_PERIOD.PM: return 'après-midi'
  }
}
/**
 * @enum {typeof PROOF_STATUS[keyof typeof PROOF_STATUS]}
 * @readonly
 */
export const PROOF_STATUS = /** @type {const} */ ({
  NOT_YET_UPLOADED: 'not_sent',
  NOT_NEEDED: 'not_asked',
  UPLOADED_BY_COLLABORATOR: 'sent_by_employee',
  UPLOADED_BY_EMPLOYER: 'sent_by_employer',
})
/**
 * @desc Whether a `LeaveRequest` has Proof Document object data
 * @type {(_: LeaveRequest) => boolean }
 */
export const hasProofDocumentData = pipe(
  prop('proof_document'),
  both(
    compose(equals('object'), typeOf),
    notEmpty
  ),
)
/**
 * @desc
 *    Returns the `LeaveRequest` Proof Document UX icon variant
 *
 *    `Maybe.Nothing` meaning no icon at all
 *
 * @type {(leaveRequest: LeaveRequest) => import('~/utils/lib/core/prelude_').Maybe<VARIANT>}
 */
export const getProofDocumentIconVariant = leaveRequest => {
  if (not(hasProofDocumentData(leaveRequest)))
    return Maybe.Nothing()

  const /** @type {STATUS_} */ status_ = prop('status_', leaveRequest)

  const isDisabledStatus = includes(
    status_,
    [
      STATUS_.CANCELLED,
      STATUS_.REJECTED
    ]
  )

  if (isDisabledStatus)
    return Maybe.Nothing()

  switch (leaveRequest.proof_document?.status) {

    case PROOF_STATUS.NOT_YET_UPLOADED:
      return Maybe.Just(VARIANT.WARNING)

    case PROOF_STATUS.UPLOADED_BY_COLLABORATOR:
    case PROOF_STATUS.UPLOADED_BY_EMPLOYER:
      return Maybe.Just(VARIANT.INFO)

    default:
      return Maybe.Nothing()

  }
}
/**
 * @enum {typeof DAY_ESTIMATION_MODE[keyof typeof DAY_ESTIMATION_MODE]} DAY_ESTIMATION_MODE
 */
export const DAY_ESTIMATION_MODE = /** @type {const} */ ({
  COMPANY_DAY_CALCULATION_MODE: 'company-day-calculation-mode',
  MISC_DAY_COUNT_MODE: 'day-count-mode',
})

export const getTokenApprovalRoute = (
  /** @type {import('~/stores/client/access').Structure["uuidData"]} */ uuidData,
  /** @type {import('~/stores/client/access').Company["code_societe"]} */ code_societe,
  /** @type {string} */ token,
  ) => concatAll([
    `/landing`,
    `/leave-request-token-approval`,
    `?uuidData=${uuidData}`,
    `&code_societe=${code_societe}`,
    `&token=${token}`
  ])
